import { DecoratorNode, $applyNodeReplacement } from 'lexical';
import { Suspense, lazy } from 'react';

const EquationComponent = lazy(() => import('./EquationComponent'));

function convertEquationElement(domNode) {
  let equation = domNode.getAttribute('data-lexical-equation');
  const inline = domNode.getAttribute('data-lexical-inline') === 'true';
  if (equation === null) equation = '';
  const node = $createEquationNode(equation, inline);
  return { node };
}

export class EquationNode extends DecoratorNode {
  __equation;
  __inline;

  static getType() {
    return 'equation';
  }

  static clone(node) {
    return new EquationNode(node.__equation, node.__inline, node.__key);
  }

  constructor(equation, inline, key) {
    super(key);
    this.__equation = equation;
    this.__inline = inline ?? false;
  }

  static importJSON(serializedNode) {
    return $createEquationNode(serializedNode.equation, serializedNode.inline);
  }

  exportJSON() {
    return {
      equation: this.__equation,
      inline: this.__inline,
      type: 'equation',
      version: 1,
    };
  }

  createDOM(config) {
    const element = this.__inline
      ? document.createElement('span')
      : document.createElement('div');
    element.className = 'editor-equation';
    return element;
  }

  exportDOM() {
    const element = document.createElement(this.__inline ? 'span' : 'div');
    element.setAttribute('data-lexical-equation', this.__equation);
    element.setAttribute('data-lexical-inline', `${this.__inline}`);
    element.textContent = this.__equation;
    return { element };
  }

  static importDOM() {
    return {
      div: (domNode) => {
        if (!domNode.hasAttribute('data-lexical-equation')) return null;
        return {
          conversion: convertEquationElement,
          priority: 2,
        };
      },
      span: (domNode) => {
        if (!domNode.hasAttribute('data-lexical-equation')) return null;
        return {
          conversion: convertEquationElement,
          priority: 1,
        };
      },
    };
  }

  updateDOM(prevNode) {
    return this.__inline !== prevNode.__inline;
  }

  getTextContent() {
    return this.__equation;
  }

  getEquation() {
    return this.__equation;
  }

  setEquation(equation) {
    const writable = this.getWritable();
    writable.__equation = equation;
  }

  getInline() {
    return this.__inline;
  }

  decorate() {
    return (
      <Suspense fallback={null}>
        <EquationComponent
          equation={this.__equation}
          inline={this.__inline}
          nodeKey={this.__key}
        />
      </Suspense>
    );
  }
}

export function $createEquationNode(equation = '', inline = false) {
  return $applyNodeReplacement(new EquationNode(equation, inline));
}

export function $isEquationNode(node) {
  return node instanceof EquationNode;
}
